import type { SurfaceDecision, SurfaceName } from './kernel-contract.js';
import { applyDesignSystem } from './design-system.js';
import { SURFACE_UNREGISTERED } from './boot-error.js';

/**
 * The UI-layer host (CAOS-125 ADR / CAOS-130).
 *
 * The host is the pixel layer over the pixel-free kernel. It receives a {@link SurfaceDecision}
 * — the kernel's structured decision about WHICH pre-install surface must appear — as DATA,
 * applies the decision's resolved design-system tokens at the document root before first
 * paint, and dispatches to the registered renderer for that surface. It re-implements NO
 * kernel logic: it does not decide the surface, it renders the one the kernel chose.
 *
 * This module owns the CONTRACT the individual surface renderers plug into (sign-in CAOS-132,
 * empty-workspace CAOS-133, and any hero/line-art surface CAOS-131): a {@link SurfaceRenderer}
 * declares which surface it paints and receives the narrowed decision plus a
 * {@link SurfaceContext}. Register renderers, then hand the host a decision.
 */

/** The narrowed decision branch for a given surface name. */
export type DecisionFor<S extends SurfaceName> = Extract<SurfaceDecision, { surface: S }>;

/**
 * The surfaces a renderer can be registered for. This now INCLUDES `hand-to-shell` (CAOS-243): the
 * frame renderer registers for it and assembles the shell chrome from the decision's
 * {@link ShellFramePlan}. With no `hand-to-shell` renderer registered (or a bare decision that
 * carries no plan), the host still stands aside — the legacy behaviour is preserved.
 */
export type RenderableSurface = SurfaceName;

/**
 * What a surface renderer is handed: the host-owned element to paint into, and the document
 * root where the design tokens have already been stamped. Renderers paint with `@caos/ui`
 * web components and read the inherited `--caos-*` tokens; they never touch the kernel.
 */
export interface SurfaceContext {
  /** The single host-owned mount to render into. The host clears it before each render. */
  readonly mount: HTMLElement;
  /** The document root the active design system's tokens are stamped on. */
  readonly root: HTMLElement;
}

/**
 * The plug-in contract every pre-install surface renderer implements. `surface` is the
 * discriminator the host dispatches on; `render` receives the decision already narrowed to
 * that branch, so a renderer reads its own typed payload (`auth`, `org`/`actions`, `stage`/
 * `issues`) with no casting.
 */
export interface SurfaceRenderer<S extends RenderableSurface = RenderableSurface> {
  readonly surface: S;
  render(decision: DecisionFor<S>, ctx: SurfaceContext): void;
}

export class SurfaceHost {
  private readonly renderers = new Map<RenderableSurface, SurfaceRenderer<RenderableSurface>>();
  private readonly mount: HTMLElement;
  private readonly root: HTMLElement;

  constructor(mount: HTMLElement, root: HTMLElement = document.documentElement) {
    this.mount = mount;
    this.root = root;
  }

  /** Register a surface renderer. Later registrations replace an earlier one for the same surface. */
  register<S extends RenderableSurface>(renderer: SurfaceRenderer<S>): this {
    // A `SurfaceRenderer<S>` is stored under the loosened `SurfaceRenderer<RenderableSurface>`
    // key; `render` is contravariant in the decision, so widen through `unknown`. Safe because
    // dispatch only ever hands this renderer the decision whose `surface` it was keyed by.
    this.renderers.set(renderer.surface, renderer as unknown as SurfaceRenderer<RenderableSurface>);
    return this;
  }

  /**
   * Render a kernel decision: apply its design system at root, then dispatch to the matching
   * surface renderer. `hand-to-shell` carries no design and is not painted here — the host
   * steps aside for the shell package.
   */
  render(decision: SurfaceDecision): void {
    if (decision.surface === 'hand-to-shell') {
      // CAOS-243: a shell master resolved. If a frame renderer is registered AND the decision
      // carries a frame plan, assemble the chrome (stamping the plan's theme first). Otherwise
      // stand aside — the legacy behaviour, preserved for a bare hand-to-shell decision.
      const frame = this.renderers.get('hand-to-shell');
      if (!frame || !decision.shell) {
        this.standAside();
        return;
      }
      applyDesignSystem(decision.shell.design, this.root);
      this.root.setAttribute('data-surface', 'hand-to-shell');
      this.mount.replaceChildren();
      frame.render(decision, { mount: this.mount, root: this.root });
      return;
    }

    // 1) Stamp the resolved tokens at root BEFORE painting — no flash of a default brand.
    applyDesignSystem(decision.design, this.root);
    this.root.setAttribute('data-surface', decision.surface);

    // 2) Dispatch to the registered renderer for this surface.
    this.mount.replaceChildren();
    const renderer = this.renderers.get(decision.surface);
    if (!renderer) {
      this.renderUnregistered(decision.surface);
      return;
    }
    // `decision` is narrowed to the renderable union by the guard above, which is exactly the
    // renderer's declared `DecisionFor<RenderableSurface>` param — no cast needed.
    renderer.render(decision, { mount: this.mount, root: this.root });
  }

  /** The kernel handed control to an installed shell; the pre-install host renders nothing. */
  private standAside(): void {
    this.root.setAttribute('data-surface', 'hand-to-shell');
    this.mount.replaceChildren();
  }

  /**
   * A surface the kernel decided on has no registered renderer. Fail loud and visible (never a
   * blank screen) with a minimal token-driven `@caos/ui` panel, so a missing plug-in is caught
   * in development rather than shipped as an empty page.
   */
  private renderUnregistered(surface: RenderableSurface): void {
    // CAOS-662 — the code comes from the workspace's normalizer, under a class the catalogue
    // names, rather than a `surface.unregistered.<name>` string namespaced under nothing.
    // CAOS-663 — this screen can reach a person (a decision naming a surface this build does not
    // ship), so its heading says what happened in their words, like the error screen's. Which
    // surface it was is kept as `data-unregistered-surface`, where inspecting the page finds it.
    const panel = document.createElement('caos-error-panel');
    panel.setAttribute('heading', "This page couldn't open");
    panel.setAttribute('data-unregistered-surface', surface);
    // CAOS-663 — no eyebrow of its own: "Host" named our plumbing. The panel's default stands.
    panel.setAttribute('code', SURFACE_UNREGISTERED.code);
    panel.setAttribute('fault', SURFACE_UNREGISTERED.fault);
    if (SURFACE_UNREGISTERED.retriable) panel.setAttribute('retriable', '');
    panel.append(document.createTextNode(SURFACE_UNREGISTERED.message));
    this.mount.append(panel);
  }
}
